import { setContext } from '@apollo/client/link/context';
import { default as ApolloLinkTimeout } from 'apollo-link-timeout';
import React from 'react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
} from '@apollo/client';
import { possibleTypes } from 'App/possibleTypes';
import { onError } from '@apollo/client/link/error';
import { useAuth0 } from '@auth0/auth0-react';
import { getChannel } from 'utils/getChannel';
import { getMSTeamsDevice, isMSTeams } from 'utils/MSTeams';
import { getTimeZone } from 'utils/dates';
import { SESSION_ID_KEY } from '../Tracking/serverside';
import { getUserLocale, shouldResetLocale } from '../../i18n/getUserLocale';
import { BEGatewayQueryContext } from '../../utils/apollo';
import { AUTHENTICATED_QUERY } from './authenticatedQuery';

export type GraphQLError = {
  name: string;
  message?: string;
};

export const getRequestId = (): string =>
  Math.random().toString(36).substr(2, 9);

const AuthorizedApolloProvider = ({ children }: { children: JSX.Element }) => {
  const { getAccessTokenSilently, isAuthenticated: isAuthenticatedViaAuth0 } =
    useAuth0();

  const staticUserLocale = getUserLocale();
  const userLocale = () =>
    shouldResetLocale() ? getUserLocale() : staticUserLocale;

  const httpLink = new HttpLink({
    uri: process.env.REACT_APP_GRAPHQL_URI,
  });

  const beGatewayHttpLink = new HttpLink({
    uri: process.env.REACT_APP_BE_GATEWAY_URI,
  });

  /*
    ToDo: The possibleTypes data should be auto-generated in future.
    If there are any changes related to union or interface types in your schema, you will have to update the fragment matcher accordingly.
    More details here:
    https://www.apollographql.com/docs/react/data/fragments/#generating-possibletypes-automatically
  */
  const cache = new InMemoryCache({ possibleTypes });

  const authLink = setContext(async (_, { headers }) => {
    // this is the JWT that we got from logging in
    // if we're using SSO, send that token to the server
    if (isAuthenticatedViaAuth0) {
      try {
        const token = await getAccessTokenSilently();

        return {
          headers: {
            ...headers,
            ['x-api-authorization']: `Bearer ${token}`,
          },
        };
      } catch (e) {
        // TODO: log auth0 token errors to somewhere useful (e.g. sentry)
        console.error(e);
      }
    }

    // otherwise, send the usual string token to the server
    return {
      headers: {
        ...headers,
        'x-api-key': localStorage.getItem('token'),
      },
    };
  });

  const interactionSessionLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'client-platform': isMSTeams() ? 'ms-teams' : 'web',
      'client-os': isMSTeams() ? getMSTeamsDevice() : 'web',
      'x-interaction-session': localStorage.getItem(SESSION_ID_KEY) || null,
    },
  }));

  const userLocaleLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'x-user-locale': userLocale(),
    },
  }));

  const channelPartnerLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'x-channel-partner': getChannel().key,
    },
  }));

  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors && graphQLErrors.length > 0) {
      if (
        graphQLErrors.findIndex(error => error.name === 'UnauthorizedError') >=
          0 ||
        graphQLErrors.findIndex(error => error.name === 'TokenExpiredError') >=
          0
      ) {
        // only attempt to log a user out when a graphql token is invalid
        // if they're not using SSO for authentication
        if (!isAuthenticatedViaAuth0) {
          cache.writeQuery({
            data: {
              authenticated: false,
            },
            query: AUTHENTICATED_QUERY,
          });
        }
      }
    }
  });

  const timeoutLink = new ApolloLinkTimeout(10000);

  /**
   * Apollo server headers (not required for client)
   */
  const apolloHeadersLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'apollographql-client-name': 'web',
      'apollographql-client-version': '2.0.0',
    },
  }));

  /**
   * GraphqlAPI client and headers
   */
  const graphQlAPILinks = ApolloLink.from([
    apolloHeadersLink,
    timeoutLink.concat(interactionSessionLink),
    userLocaleLink,
    channelPartnerLink,
    errorLink,
    timeoutLink.concat(authLink),
    timeoutLink.concat(httpLink),
  ]);

  /**
   * Be-Gateway API client and headers
   */
  const beGatewayHeadersLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'x-request-id': getRequestId(),
      'x-user-tz': getTimeZone(),
    },
  }));

  const beGatewayAPILinks = ApolloLink.from([
    apolloHeadersLink,
    beGatewayHeadersLink,
    userLocaleLink,
    errorLink,
    timeoutLink.concat(authLink),
    timeoutLink.concat(beGatewayHttpLink),
  ]);

  const client = new ApolloClient({
    cache,
    connectToDevTools: process.env.NODE_ENV === 'development',
    defaultOptions: {
      query: {
        fetchPolicy: 'network-only',
      },
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
    link: ApolloLink.split(
      operation =>
        operation.getContext().clientName ===
        BEGatewayQueryContext.context.clientName,
      beGatewayAPILinks,
      graphQlAPILinks,
    ),
    resolvers: {},
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default AuthorizedApolloProvider;
